import React, {useContext, useEffect, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {userAPI} from "../Redux/services/UserService";
import {useNavigate} from "react-router-dom";
import {categoryAPI} from "../Redux/services/CategoryService";
import {AuthContext} from "../Infrastructure/provider/AuthProvider";
import MultiSelect from "../ui/MultiSelect";

interface Category {
    id: number;
    title: string;
}

interface FormValues {
    categoryId: number[];
}

const AdminPage: React.FC = () => {
    const {data: users, isLoading, isError, refetch: refetchUsers} = userAPI.useFetchUsersQuery("");
    const {data: categories, refetch: refetchCategories} = categoryAPI.useFetchAllCategoriesQuery("");
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const navigate = useNavigate();
    const {user: currentUser} = useContext(AuthContext);
    const [assignCategory] = userAPI.useAssignCategoryMutation();
    const [removeCategory] = userAPI.useRemoveCategoryMutation();
    const [deleteUser] = userAPI.useDeleteUserMutation();

    const {control, handleSubmit, reset} = useForm<FormValues>({});
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [isMobileView, setIsMobileView] = useState(false);
    const [showUserForm, setShowUserForm] = useState(false);

    useEffect(() => {
        const checkIsMobile = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobileView(mobile);

            if (!mobile) {
                setShowUserForm(false);
            }
        };

        checkIsMobile();
        window.addEventListener("resize", checkIsMobile);
        return () => window.removeEventListener("resize", checkIsMobile);
    }, []);

    if (isLoading) return <div className="flex items-center justify-center h-screen text-gray-600">Завантаження...</div>;
    if (isError) return <div className="flex items-center justify-center h-screen text-red-500">Помилка при завантаженні користувачів.</div>;

    const handleEdit = (userId: number) => navigate(`/editUser/${userId}`);

    const handleDelete = (userId: number) => {
        if (window.confirm("Ви впевнені, що хочете видалити користувача?")) {
            deleteUser(userId);
        }
    };

    const handleCreate = () => navigate("/createUser");

    const filteredUsers = users
        .filter((user: any) => user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
        .filter((user: any) => (roleFilter ? user.role === roleFilter : true))
        .sort((a: any, b: any) => a.full_name.localeCompare(b.full_name));

    const onSubmit = async (data: FormValues, userId: number) => {
        try {
            await assignCategory({id: userId, body: {category_ids: data.categoryId}})
            refetchUsers();
            refetchCategories();
        }
        catch (error) {
            console.log(error)
        }
    };

    const removeAccess = async (id: any) => {
        try {
            await removeCategory(id);
            refetchUsers();
            refetchCategories();
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <div className="flex h-[85vh] bg-gray-100 rounded-xl overflow-hidden shadow-md">

            {(!isMobileView || !showUserForm) && (
                <div className="w-full lg:w-1/3 bg-white border-r border-gray-300 flex flex-col">

                    <div className="flex justify-between items-center p-4 border-b border-gray-300">
                        <h2 className="text-xl font-semibold">Користувачі</h2>
                        <button
                            onClick={handleCreate}
                            className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm"
                        >
                            Створити
                        </button>
                    </div>

                    <div className="flex gap-2 p-4 border-b border-gray-200">
                        <input
                            type="text"
                            placeholder="Пошук..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <select
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">Всі ролі</option>
                            <option value="admin">Адмін</option>
                            <option value="subadmin">Субадмін</option>
                            <option value="user">Користувач</option>
                        </select>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {filteredUsers.map((user: any) => (
                            <div
                                key={user.id}
                                className={`p-4 cursor-pointer hover:bg-gray-100 ${selectedUserId === user.id ? "bg-gray-200" : ""}`}
                                onClick={() => {
                                    setSelectedUserId(user.id);

                                    // ✅ Подставляем категории субадмина
                                    reset({
                                        categoryId: user.subadmin_categories?.map((c: any) => c.id) || []
                                    });

                                    if (isMobileView) setShowUserForm(true);
                                }}
                            >
                                <p className="font-semibold">{user.full_name}</p>
                                <p className="text-sm text-gray-500">{user.role}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {(!isMobileView || showUserForm) && (
                <div className="flex-1 p-6 overflow-y-auto">

                    {isMobileView && (
                        <button
                            className="mb-4 text-blue-600 text-lg flex items-center gap-2"
                            onClick={() => setShowUserForm(false)}
                        >
                            ← Назад
                        </button>
                    )}

                    {selectedUserId ? (
                        users
                            .filter((user: any) => user.id === selectedUserId)
                            .map((user: any) => (
                                <form
                                    key={user.id}
                                    onSubmit={handleSubmit((data) => onSubmit(data, user.id))}
                                    className="bg-white rounded-xl shadow-md p-6 space-y-4"
                                >
                                    <div className="flex justify-between items-center">
                                        <h2 className="text-2xl font-semibold">{user.full_name}</h2>
                                        <p className="text-gray-500">{user.role}</p>
                                    </div>

                                    {user.role !== "admin" && (
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                            <label className="text-gray-700 font-medium min-w-[100px]">Категорії:</label>
                                            <Controller
                                                name="categoryId"
                                                control={control}
                                                render={({ field }) => (
                                                    <MultiSelect
                                                        value={field.value || []}
                                                        onChange={(newValue) => field.onChange(newValue)}
                                                        categories={categories}
                                                    />
                                                )}
                                            />
                                        </div>
                                    )}

                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            type="button"
                                            onClick={() => handleEdit(user.id)}
                                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                                        >
                                            Редагувати
                                        </button>

                                        {currentUser.id !== selectedUserId && (
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(user.id)}
                                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                                            >
                                                Видалити
                                            </button>
                                        )}

                                        {user.role !== "admin" && (
                                            <button
                                                type="submit"
                                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                                            >
                                                Зберегти права
                                            </button>
                                        )}

                                        {user.role !== "user" && (
                                            <button
                                                type="button"
                                                onClick={() => removeAccess(user.id)}
                                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                                            >
                                                Видалити права
                                            </button>
                                        )}
                                    </div>
                                </form>
                            ))
                    ) : (
                        <p className="text-gray-500">Оберіть користувача зі списку.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminPage;
